/**
 * TESORERÍA OPERATIVA (V1.46) — flujo de caja pasado/presente/futuro.
 * Gris = pagado · Rojo = vencido no pagado · Sin color = futuro.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wallet, AlertTriangle, TrendingUp, ShoppingCart, ChevronDown, ChevronRight } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const eur = (v) => (v ?? 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const eur2 = (v) => (v ?? 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const monthLabel = (m) => `${MESES[parseInt(m.slice(5), 10) - 1]} ${m.slice(2, 4)}`;
const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const endOfYear = () => `${new Date().getFullYear()}-12-31`;

const CELL_STYLE = {
  pagado: "bg-secondary/70 text-muted-foreground",
  vencido: "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400 font-semibold",
  futuro: "",
};

function Cell({ cell, negative }) {
  if (!cell) return <td className="px-2 py-1 text-right text-xs text-muted-foreground/40">—</td>;
  return (
    <td className={`px-2 py-1 text-right text-xs tabular whitespace-nowrap ${CELL_STYLE[cell.status] || ""}`} title={cell.status}>
      {negative ? "−" : ""}{eur2(cell.amount)}
    </td>
  );
}

function MatrixSection({ title, rows, months, blocks, negative, testId }) {
  const byBlock = useMemo(() => {
    const map = {};
    rows.forEach((r) => (map[r.block] = map[r.block] || []).push(r));
    return map;
  }, [rows]);
  if (rows.length === 0) return null;
  return (
    <>
      <tr><td colSpan={months.length + 2} className="px-2 pt-3 pb-1 text-[11px] uppercase tracking-wider font-bold text-muted-foreground">{title}</td></tr>
      {blocks.filter((b) => byBlock[b]).map((b) => (
        <FragmentBlock key={`${testId}-${b}`} block={b} rows={byBlock[b]} months={months} negative={negative} testId={testId} />
      ))}
    </>
  );
}

function FragmentBlock({ block, rows, months, negative, testId }) {
  return (
    <>
      <tr className="bg-secondary/40">
        <td colSpan={months.length + 2} className="px-2 py-1 text-xs font-bold" data-testid={`${testId}-block-${block}`}>{block}</td>
      </tr>
      {rows.map((r) => (
        <tr key={r.name + r.block} className="border-b border-border/50 hover:bg-secondary/20">
          <td className="px-2 py-1 text-xs font-medium sticky left-0 bg-background whitespace-nowrap max-w-[180px] truncate" title={r.name}>{r.name}</td>
          {months.map((m) => <Cell key={m} cell={r.cells[m]} negative={negative} />)}
          <td className="px-2 py-1 text-right text-xs font-semibold tabular whitespace-nowrap">{negative ? "−" : ""}{eur2(r.total)}</td>
        </tr>
      ))}
    </>
  );
}

function ProjectionCard({ p }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-lg p-3 min-w-[170px]" data-testid={`proj-${p.label}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{p.label}</div>
      <div className={`text-lg font-heading font-extrabold tabular ${p.saldo < 0 ? "text-red-600" : ""}`}>{eur(p.saldo)}</div>
      <button onClick={() => setOpen(!open)} className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-0.5 mt-1" data-testid={`proj-motivos-${p.label}`}>
        {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />} Motivos
      </button>
      {open && (
        <div className="mt-1.5 space-y-0.5 text-[11px]">
          {p.motivos.ingresos.map((i) => <div key={"i" + i.nombre} className="flex justify-between gap-2 text-emerald-700"><span className="truncate">+ {i.nombre}</span><span className="tabular shrink-0">{eur(i.importe)}</span></div>)}
          {p.motivos.pagos.map((g) => <div key={"p" + g.nombre} className="flex justify-between gap-2 text-red-600"><span className="truncate">− {g.nombre}</span><span className="tabular shrink-0">{eur(g.importe)}</span></div>)}
          {p.motivos.ingresos.length === 0 && p.motivos.pagos.length === 0 && <div className="text-muted-foreground">Sin flujos hasta esta fecha.</div>}
        </div>
      )}
    </div>
  );
}

export default function Tesoreria() {
  const [desde, setDesde] = useState(firstOfYear());
  const [hasta, setHasta] = useState(endOfYear());
  const [data, setData] = useState(null);
  const [importe, setImporte] = useState("");
  const [compra, setCompra] = useState(null);
  const [showVencidos, setShowVencidos] = useState(false);

  useEffect(() => {
    api.get("/tesoreria/overview", { params: { desde, hasta } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [desde, hasta]);

  const simular = async () => {
    const amt = parseFloat(String(importe).replace(",", "."));
    if (!amt || amt <= 0) return;
    try {
      const r = await api.post("/tesoreria/compra", { amount: amt });
      setCompra(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="tesoreria-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2"><Wallet size={26} /> Tesorería Operativa</h1>
          <p className="text-sm text-muted-foreground mt-1">Pasado, presente y futuro de tu caja. <span className="inline-block px-1.5 rounded bg-secondary/70 text-muted-foreground">Gris = pagado</span> · <span className="inline-block px-1.5 rounded bg-red-100 text-red-700">Rojo = vencido</span> · Sin color = futuro.</p>
        </div>
        <div className="flex gap-2">
          <Input type="date" data-testid="tes-desde" className="w-[150px]" value={desde} onChange={(e) => setDesde(e.target.value)} />
          <Input type="date" data-testid="tes-hasta" className="w-[150px]" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="border border-border rounded-lg p-4" data-testid="caja-bancaria">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Caja Bancaria</div>
              <div className="text-2xl font-heading font-extrabold tabular">{eur(data.caja.bancaria)}</div>
              <div className="text-[11px] text-muted-foreground">Dinero disponible hoy en cuentas líquidas</div>
            </div>
            <div className="border border-border rounded-lg p-4" data-testid="caja-operativa">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Caja Operativa</div>
              <div className={`text-2xl font-heading font-extrabold tabular ${data.caja.operativa < data.caja.bancaria ? "text-amber-600" : ""}`}>{eur(data.caja.operativa)}</div>
              {data.caja.vencidos_total > 0 ? (
                <button onClick={() => setShowVencidos(!showVencidos)} className="text-[11px] text-red-600 flex items-center gap-1" data-testid="vencidos-toggle">
                  <AlertTriangle size={11} /> {eur2(data.caja.vencidos_total)} en pagos vencidos sin registrar {showVencidos ? "▲" : "▼"}
                </button>
              ) : (
                <div className="text-[11px] text-muted-foreground">Sin pagos vencidos pendientes</div>
              )}
            </div>
            <div className="border border-border rounded-lg p-4" data-testid="caja-proyectada">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Caja Proyectada (fin de año)</div>
              <div className={`text-2xl font-heading font-extrabold tabular ${(data.proyeccion.at(-1)?.saldo ?? 0) < 0 ? "text-red-600" : "text-emerald-600"}`}>{eur(data.proyeccion.at(-1)?.saldo)}</div>
              <div className="text-[11px] text-muted-foreground">Con ingresos y pagos previstos</div>
            </div>
          </div>

          {showVencidos && data.caja.vencidos.length > 0 && (
            <div className="border border-red-200 bg-red-50/50 dark:bg-red-950/20 rounded-lg p-3 text-xs space-y-1" data-testid="vencidos-detalle">
              {data.caja.vencidos.map((v, i) => (
                <div key={i} className="flex justify-between"><span>{v.fecha} · {v.nombre}</span><span className="tabular font-semibold text-red-700">{eur2(v.importe)}</span></div>
              ))}
              <div className="text-[11px] text-muted-foreground pt-1">Márcalos como pagados en "Pagos" si ya los abonaste, o regularízalos para recuperar caja operativa.</div>
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-2 flex items-center gap-1.5"><TrendingUp size={13} /> Proyección de caja</div>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {data.proyeccion.map((p) => <ProjectionCard key={p.label} p={p} />)}
            </div>
          </div>

          <div className="border border-border rounded-lg p-4" data-testid="compra-simulador">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-2 flex items-center gap-1.5"><ShoppingCart size={13} /> ¿Puedo permitirme una compra?</div>
            <div className="flex gap-2 max-w-md">
              <Input data-testid="compra-importe" inputMode="decimal" placeholder="Importe (ej. 1200)" value={importe} onChange={(e) => setImporte(e.target.value)} onKeyDown={(e) => e.key === "Enter" && simular()} />
              <Button data-testid="compra-simular" onClick={simular} disabled={!importe}>Simular</Button>
            </div>
            {compra && (
              <div className="mt-3 text-sm space-y-1" data-testid="compra-resultado">
                {compra.resultado === "posible" ? (
                  <div className="font-semibold text-emerald-600">✔ Compra posible</div>
                ) : (
                  <div className="font-semibold text-red-600">✘ No recomendable</div>
                )}
                <div className="text-xs text-muted-foreground">
                  Impacto: la caja prevista a fin de año pasaría de <span className="font-semibold text-foreground">{eur2(compra.caja_prevista_fin_ano)}</span> a <span className="font-semibold text-foreground">{eur2(compra.impacto.caja_fin_ano_tras_compra)}</span>. Saldo mínimo previsto: <span className={`font-semibold ${compra.impacto.saldo_minimo < 0 ? "text-red-600" : "text-foreground"}`}>{eur2(compra.impacto.saldo_minimo)}</span> ({compra.impacto.fecha_saldo_minimo}).
                </div>
                {compra.motivo && (
                  <div className="text-xs">
                    <span className="text-red-600 font-medium">Déficit previsto de {eur2(compra.motivo.deficit)} en {compra.motivo.mes_deficit}, provocado por:</span>
                    <ul className="list-disc ml-5 text-muted-foreground">
                      {compra.motivo.provocado_por.map((c) => <li key={c.nombre}>{c.nombre} ({eur2(c.importe)})</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border border-border rounded-lg overflow-x-auto" data-testid="tesoreria-matriz">
            <table className="w-full border-collapse min-w-[720px]">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  <th className="px-2 py-2 text-left text-[11px] uppercase tracking-wider text-muted-foreground sticky left-0 bg-secondary/30">Concepto</th>
                  {data.months.map((m) => <th key={m} className="px-2 py-2 text-right text-[11px] uppercase tracking-wider text-muted-foreground whitespace-nowrap">{monthLabel(m)}</th>)}
                  <th className="px-2 py-2 text-right text-[11px] uppercase tracking-wider text-muted-foreground">Total</th>
                </tr>
              </thead>
              <tbody>
                <MatrixSection title="↑ Ingresos previstos" rows={data.ingresos} months={data.months} blocks={data.blocks} negative={false} testId="ing" />
                <MatrixSection title="↓ Pagos (compromisos · obligaciones · seguros)" rows={data.pagos} months={data.months} blocks={data.blocks} negative testId="pag" />
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border bg-secondary/40 font-semibold">
                  <td className="px-2 py-2 text-xs sticky left-0 bg-secondary/40">Neto del mes</td>
                  {data.months.map((m) => {
                    const t = data.month_totals[m];
                    return <td key={m} className={`px-2 py-2 text-right text-xs tabular ${t?.neto < 0 ? "text-red-600" : "text-emerald-700"}`}>{eur(t?.neto)}</td>;
                  })}
                  <td />
                </tr>
              </tfoot>
            </table>
            {data.ingresos.length === 0 && data.pagos.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">Sin ingresos recurrentes ni compromisos/obligaciones/seguros en el rango. Crea compromisos e ingresos recurrentes para alimentar la tesorería.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
